import { ChangeEvent, createContext, SyntheticEvent, useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  Divider,
  ListItem,
  Paper,
  Typography,
} from "@mui/material";
import { useForm } from "react-hook-form";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { CustomFormField } from "../../../utilis/component/CustomFormElement";
import { useLocation, useNavigate } from "react-router-dom";
import TemplateList from "./TemplateList";
import {
  CptListType,
  initialField,
  initialFormData,
  LoincListType,
  MasterFormData,
  MasterTableDataType,
  TemplateFieldType,
  TemplateListContextType,
} from "./types";
import { useMutation } from "react-query";
import {
  getAllCpt,
  getAllFields,
  getAllLoinc,
  saveMasterData,
} from "./services";
import debounce from "../../../utilis/methods/networkHandling";
import usePopup from "../../../hooks/usePopup";
export const TemplateListContext = createContext<TemplateListContextType>({
  fullList: [],
  setFullList: () => {},
  edit: initialField,
  setEdit: () => {},
});
const pageTitle = "Observation template";

function ObservationTemplateAddOrEditPage() {
  const navigate = useNavigate();
  const { showSnackbar } = usePopup();
  const { control, handleSubmit, reset } = useForm<MasterFormData>({
    defaultValues: initialFormData,
  });
  const [fullList, setFullList] = useState<TemplateFieldType[]>([]);
  const [edit, setEdit] = useState<TemplateFieldType>(initialField);
  const {
    state: { data },
  } = useLocation() as { state: { data: MasterTableDataType | null } };

  const {
    mutate: searchCpt,
    data: cptData,
    isLoading: cptLoading,
  } = useMutation(getAllCpt);
  const {
    mutate: searchLoinc,
    data: loincData,
    isLoading: loincLoading,
  } = useMutation(getAllLoinc);
  const { mutate: fetchFields, } = useMutation(
    getAllFields,
    {
      onSuccess: (result) => {
        if (result.responseIndicator == "success") {
          setFullList(result.response);
        }
      },
    }
  );
  const { mutate: save, isLoading: saveLoading } = useMutation(saveMasterData, {
    onSuccess: (result) => {
      if (result.responseIndicator == "success") {
        showSnackbar({
          message: result.responseMessage,
          open: true,
          variant: "success",
          duration: 3000,
        });
        handleGoBack();
      } else {
        showSnackbar({
          message: result.responseMessage,
          open: true,
          variant: "warning",
          duration: 3000,
        });
      }
    },
    onError: (e: any) => {
      showSnackbar({
        message: e?.response?.data?.responseMessage || "Something went wrong !",
        open: true,
        variant: "error",
        duration: 3000,
      });
    },
  });

  const handleGoBack = () => {
    navigate(-1);
  };
  const handleSearch = (_: SyntheticEvent<Element, Event>, value: string,) => {
    searchCpt(value);
  };

  const handleLoincSearch = (e: ChangeEvent<HTMLInputElement>) => {
    searchLoinc(e.target.value);
  };
  const debounceSearch = debounce(handleSearch, 500);
  const debounceLoincSearch = debounce(handleLoincSearch, 500);

  const handleReset = () => {
    reset(initialFormData);
  };

  const onSubmit = (value: MasterFormData) => {
    if (fullList.length > 0) {
      save({
        cptCode: value.cpt?.cptCode as string,
        cptId: value.cpt?.cptId as number,
        createdByDate: "",
        localCode: "",
        loincId: value.loinc?.loincId || null,
        status: value?.status,
        templateId: data?.templateId || 0,
        templateName: value.templateName,
        fields: fullList,
      });
    } else {
      showSnackbar({
        message: "Please add template details",
        open: true,
        variant: "error",
        duration: 3000,
      });
    }
  };

  useEffect(() => {
    searchCpt("");
    searchLoinc("");
    if (data) {
      reset({
        cpt: {
          cptId: data.cpt_id,
          cptCode: data.cpt_code,
          cptName: data.cpt_name,
        },
        loinc:
          data.loinc_id || data.loinc_name
            ? {
                loincId: data.loinc_id as number,
                loincName: data.loinc_name as string,
              }
            : null,
        status: data.template_status,
        templateName: data.template_name,
      });
      fetchFields({ cptId: data.cpt_id, templateName: data.template_name });
    }
  }, [data]);

  return (
    <TemplateListContext.Provider
      value={{ fullList, setFullList, edit, setEdit }}
    >
      <div className="p-3">
        <Paper className="w-full">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="flex justify-between items-center p-2">
              <div>
                <Typography variant="h6" className="p-3">
                  Add {pageTitle}
                </Typography>
              </div>
              <div className="flex gap-2">
                <Button
                 
                variant="outlined"
                  disabled={saveLoading}
                 
                startIcon={<ArrowBackIcon />}
                 
                onClick={handleGoBack}
                
              >
                  back
                </Button>
                {!Boolean(data) && (
                  <Button
                    color="error"
                    type="reset"
                    disabled={saveLoading}
                    onClick={handleReset}
                    startIcon={<CloseIcon />}
                  >
                    reset
                  </Button>
                )}
                <Button
                  disabled={saveLoading}
                  type="submit"
                  color="success"
                  startIcon={
                    saveLoading ? (
                      <CircularProgress size={18} color="success" />
                    ) : (
                      <SaveIcon />
                    )
                  }
                >
                  save
                </Button>
              </div>
            </div>
            <Divider />
            <div className="p-3 grid grid-cols-4 gap-3">
              <CustomFormField
                control={control}
                name="templateName"
                element="input"
                fieldProps={{ label: "Template name" }}
                rules={{ required: "Template name required" }}
              />
              <CustomFormField
                control={control}
                name="cpt"
                element="autocomplete"
                loading={cptLoading}
                options={cptData?.response || []}
                fieldProps={{ label: "Select CPT" }}
                rules={{ required: "CPT required" }}
                autocompleteProps={{
                  getOptionLabel: (option: CptListType) =>
                    `${option.cptCode} ${option.cptName}`,
                  renderOption: (params, option: CptListType) => (
                    <ListItem
                      {...params}
                    >{`Code : ${option.cptCode} | ${option.cptName}`}</ListItem>
                  ),
                  filterOptions: (option) => option,
                  onInputChange:debounceSearch,
                }}
              />
              <CustomFormField
                control={control}
                name="loinc"
                element="autocomplete"
                loading={loincLoading}
                options={loincData?.response || []}
                fieldProps={{
                  label: "LOINC name",
                  onChange: debounceLoincSearch,
                }}
                rules={{ required: false }}
                autocompleteProps={{
                  getOptionLabel: (option: LoincListType) =>
                    `${option.loincName}`,
                  renderOption: (params, option: LoincListType) => (
                    <ListItem
                      {...params}
                    >{`Code : ${option.loincCode} | ${option.loincName}`}</ListItem>
                  ),
                  filterOptions: (option) => option,
                }}
              />
              <CustomFormField
                control={control}
                name="status"
                element="radio"
                radioProps={{ row: true }}
                radios={[
                  { label: "Active", value: 1 },
                  { label: "Inactive", value: 0 },
                ]}
                fieldProps={{ label: "Status " }}
                rules={{ required: "Status required" }}
              />
            </div>
          </form>
          <TemplateList />
        </Paper>
      </div>
    </TemplateListContext.Provider>
  );
  }

export default ObservationTemplateAddOrEditPage;
